import { useState } from 'react';
import { DialogContent, DialogDescription, DialogRoot, DialogTitle } from '@discover-ui/dialog';
import Button from '@discover-ui/button';
import Separator from '@discover-ui/separator';
import RestaurantRating from '@discover-components/restaurant/rating';
import { cn } from '@discover-lib/utils';
import getRestaurantHref from '@discover-components/restaurant/getRestaurantHref';
import parseHtml from '@/lib/parseHtml';
import Link from '@discover-ui/link';
import { REVIEW_SUMMARY_TRUNCATE_LENGTH, truncateReviewSummary } from './utils';

export type RestaurantReviewSummaryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menuTitle: string;
  slug?: string | null;
  people: number;
  rating: number;
  approvedReviewsCount: number;
  reviewSummary: string;
  reviewSummaryDetails: string;
};

export default function RestaurantReviewSummaryDialog({
  open,
  onOpenChange,
  menuTitle,
  slug,
  people,
  rating,
  approvedReviewsCount,
  reviewSummary,
  reviewSummaryDetails,
}: RestaurantReviewSummaryDialogProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const reviewsHref = getRestaurantHref(slug, people, 'reviews');
  const trimmedReviewSummary = reviewSummary.trim();
  const canExpand = trimmedReviewSummary.length > REVIEW_SUMMARY_TRUNCATE_LENGTH;
  const summaryText =
    canExpand && !isExpanded ? truncateReviewSummary(trimmedReviewSummary) : trimmedReviewSummary;

  return (
    <DialogRoot
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setIsExpanded(false);
        }
        onOpenChange(nextOpen);
      }}>
      <DialogContent className="overflow-y-auto rounded-lg p-0 md:max-w-2xl lg:max-w-3xl">
        <div className="flex flex-col gap-4 p-6 md:p-8 lg:p-10">
          <p className="m-0 font-serif text-lg text-gold">{menuTitle}</p>
          <Separator />

          <div className="flex flex-col gap-2">
            <DialogTitle className="m-0 font-serif text-3xl font-normal leading-tight text-brand-blue">
              AI Summary
            </DialogTitle>
            <DialogDescription className="m-0 text-sm text-gray-600">
              AI-powered summary from real diner reviews
            </DialogDescription>
          </div>

          <RestaurantRating
            className="no-underline hover:no-underline"
            rating={rating}
            count={approvedReviewsCount}
          />

          <div className="text-base leading-relaxed text-gray-950">
            {summaryText.split(/\n\n+/).map((paragraph, index, paragraphs) => {
              const isLastParagraph = index === paragraphs.length - 1;

              return (
                <p key={index} className={cn('m-0', index > 0 && 'mt-4')}>
                  {paragraph}
                  {isLastParagraph && canExpand && !isExpanded && (
                    <>
                      {' '}
                      <Button
                        kind="link"
                        className="inline font-semibold text-black underline-offset-2"
                        onClick={() => setIsExpanded(true)}>
                        see more
                      </Button>
                    </>
                  )}
                </p>
              );
            })}
          </div>

          <div className="m-0 text-sm text-gray-600 md:text-base">
            {parseHtml(reviewSummaryDetails.trim())}
          </div>

          <Link href={reviewsHref} onClick={() => onOpenChange(false)} kind="cta" width="wide">
            See all reviews
          </Link>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}
